import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BulkSelectEmployeeDialogComponent,
    DeprecatedConfirmationDialogComponent,
    DeprecatedModalFormComponent,
} from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffBackupApprover } from '@app/models/time-off-v3/time-off-backup-approver.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { SettingsToolTips } from '@app/modules/time-off-v3/meta/tooltip-meta';
import { TimeOffService, TimeOffTypesTable } from '@app/modules/time-off-v3/services';
import { TimeOffModuleMenuService } from '@app/modules/time-off-v3/services/time-off-module-menu.service';
import { AnalyticService, AuthService, NotifyService, SettingsService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Model } from '@models/core/base.model';
import { TimeOffApprovalFlow } from '@models/time-off-v3/time-off-approval-flow.model';
import { TimeOffPolicy } from '@models/time-off-v3/time-off-policy.model';
import { WorkSchedule } from '@models/time-off-v3/works-schedule.model';
import { AssignApprovalFlowForm } from '@time-off-v3/forms';
import { TimeOffBalanceDateSetting } from '@time-off-v3/meta/time-off-meta';
import { TimeOffApprovalFlowTable } from '@time-off-v3/services/time-off-approval-flow-table/time-off-approval-flow-table';
import { WorkScheduleTable } from '@time-off-v3/services/work-schedule-table/work-schedule-table';

@Component({
    templateUrl: './settings-show.template.html',
    styleUrls: ['./settings-show.style.scss'],
})
export class SettingsShowView implements OnInit {
    @ViewChild('appEmployeeSelection', { static: true }) appEmployeeSelection!: BulkSelectEmployeeDialogComponent;
    @ViewChild('createWorkScheduleDialog') createWorkScheduleDialog!: DeprecatedModalFormComponent;
    @ViewChild('createApprovalFlowDialog') createApprovalFlowDialog!: DeprecatedModalFormComponent;
    @ViewChild('assignApprovalFlowDialog') assignApprovalFlowDialog!: DeprecatedModalFormComponent;
    @ViewChild('assignApprovalFlowForm') assignApprovalFlowForm!: AssignApprovalFlowForm;
    @ViewChild('workScheduleDeleteModal') workScheduleDeleteModal!: DeprecatedConfirmationDialogComponent;
    @ViewChild('approvalFlowDeleteModal') approvalFlowDeleteModal!: DeprecatedConfirmationDialogComponent;
    @ViewChild('deleteTypeModal') deleteTypeModal!: DeprecatedConfirmationDialogComponent;

    settingsToolTips = SettingsToolTips;
    isLoading = false;

    workScheduleTable: WorkScheduleTable = new WorkScheduleTable(this.auth);
    timeOffApprovalFlowTable: TimeOffApprovalFlowTable = new TimeOffApprovalFlowTable(this.auth);
    timeOffTypesTable = new TimeOffTypesTable(this.auth);
    activeAdmins: Employee[] = [];

    // Default value for selected backup approver
    selectedBackupApprover = 0;

    currentTimeOffBackupApprover: TimeOffBackupApprover = new TimeOffBackupApprover();
    isTimeOffImprovementsFeatureFlagEnabled = false;

    constructor(
        private auth: AuthService,
        private settingsService: SettingsService,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private timeOffService: TimeOffService,
        private analyticService: AnalyticService,
        private featureService: FeatureService,
        public timeOffModuleMenuService: TimeOffModuleMenuService
    ) {}

    async ngOnInit(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffSettings);

        this.isTimeOffImprovementsFeatureFlagEnabled = await this.featureService.has(FeatureFlag.timeOffImprovements);

        if (!this.isTimeOffImprovementsFeatureFlagEnabled) {
            this.isLoading = true;
            this.getActiveAdmins();
            this.getDefaultApprover();
            this.isLoading = false;
        }
    }

    get timeOffBalanceDateToday(): boolean | undefined {
        return this.settingsService.getByName(TimeOffBalanceDateSetting)?.value;
    }

    set timeOffBalanceDateToday(_: boolean) {
        this.settingsService
            .getByName(TimeOffBalanceDateSetting)
            .param('company', this.auth.company.id)
            .toggle()
            .catch((err) => {
                err.message
                    ? this.notify.error(err.message)
                    : this.notify.error('time-off-v3.canNotUpdateTimeOffBalanceDateSetting');
            });
    }

    get timeOffVisibility(): boolean | undefined {
        return this.settingsService.getByName('timeOffRequests.visibility')?.value;
    }

    set timeOffVisibility(_: boolean) {
        this.settingsService
            .getByName('timeOffRequests.visibility')
            .param('company', this.auth.company.id)
            .toggle()
            .catch((err) => {
                err.message
                    ? this.notify.error(err.message)
                    : this.notify.error('time-off-v3.canNotToggleTimeOffVisibilitySetting');
            });
    }

    async onUpdateBackupApprover(matSelect: MatSelectChange): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffSettingsOverallSettingsUpdateBackupApproverField);

        if (this.currentTimeOffBackupApprover.id) {
            await this.currentTimeOffBackupApprover.delete();
            this.currentTimeOffBackupApprover = new TimeOffBackupApprover();
        }

        if (!matSelect.value) {
            return;
        }

        const timeOffBackupApprover: TimeOffBackupApprover = new TimeOffBackupApprover({ approverId: matSelect.value });

        try {
            this.currentTimeOffBackupApprover = await timeOffBackupApprover.save();
            this.notify.success('time-off-v3.timeOffBackupApproverSet');
        } catch (err) {
            this.notify.error(err.message);
        }
    }

    trackLearnMoreBackupApprover(): void {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffSettingsOverallSettingsBackupApproverLink);
    }

    async menuAction(dispatchedEvent: MenuColumnEvent): Promise<void> {
        const { menuItem, data } = dispatchedEvent;
        const model = data as Model;

        switch (menuItem.event) {
            case WorkScheduleTable.assignOption.event:
                await this.assignWorkSchedule(model);
                break;
            case WorkScheduleTable.editOption.event:
                const routePath = this.getWorkScheduleRoutePath();
                await this.router.navigate(['../work-schedule', model.id, routePath], {
                    relativeTo: this.route,
                });
                break;
            case WorkScheduleTable.deleteOption.event:
                await this.deleteWorkSchedule(model);
                break;
            case TimeOffApprovalFlowTable.editOption.event:
                await this.router.navigate(['../approval-flow', model.id, 'edit'], {
                    relativeTo: this.route,
                });
                break;
            case TimeOffApprovalFlowTable.assignOption.event:
                // eslint-disable-next-line no-case-declarations
                const approvalFlow = data as TimeOffApprovalFlow;
                this.assignApprovalFlowForm.approvalFlow = approvalFlow;
                this.assignApprovalFlowDialog.show();
                break;
            case TimeOffApprovalFlowTable.deleteOption.event:
                await this.deleteApprovalFlow(model);
                break;
            case TimeOffTypesTable.editOption.event:
                await this.router.navigate(['/time-off', 'v3', 'types-and-policies', 'types', model.id, 'edit'], {
                    relativeTo: this.route,
                });
                break;
            case TimeOffTypesTable.deleteOption.event:
                await this.onDeleteTimeOffType(model);
                break;
        }
    }

    onCreateWorkScheduleClick(): void {
        this.createWorkScheduleDialog.show();
    }

    onCreateApprovalFlowClick(): void {
        this.createApprovalFlowDialog.show();
    }

    onCreateTimeOffTypeClick(): void {
        this.router.navigate(['/time-off', 'v3', 'types-and-policies', 'types', 'create']);
    }

    async createWorkScheduleAndRedirect(workSchedule: WorkSchedule): Promise<void> {
        this.isLoading = true;

        try {
            workSchedule.isDefault = false;
            workSchedule.weekendDays = ['Saturday', 'Sunday'];
            const routePath = this.getWorkScheduleRoutePath();
            await workSchedule.save();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffSettingsEditOrCreateWorkSchedule);
            await this.router.navigate(['../work-schedule', workSchedule.id, routePath], {
                relativeTo: this.route,
            });
        } catch (err) {
            err.message ? this.notify.error(err.message) : this.notify.error('time-off-v3.workScheduleCreationFailed');
        }

        this.isLoading = false;
    }

    async createApprovalFlowAndRedirect(timeOffApprovalFlow: TimeOffApprovalFlow): Promise<void> {
        this.isLoading = true;

        try {
            timeOffApprovalFlow.companyId = this.auth.company.id;
            timeOffApprovalFlow.isAutomatic = false;
            await timeOffApprovalFlow.save();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffCreateApprovalFlow);
            await this.router.navigate(['../approval-flow', timeOffApprovalFlow.id, 'edit'], {
                relativeTo: this.route,
            });
        } catch (err) {
            err.message ? this.notify.error(err.message) : this.notify.error('time-off-v3.approvalFlowCreationFailed');
        }

        this.isLoading = false;
    }

    async onAssignApprovalFlows(): Promise<void> {
        this.isLoading = true;

        try {
            const approvalFlowAssignmentPromises = this.assignApprovalFlowForm.selectedPolicies.map(
                (policy: TimeOffPolicy) => {
                    policy.timeOffApprovalFlowId = this.assignApprovalFlowForm.approvalFlow.id;

                    return policy.save();
                }
            );

            await Promise.all(approvalFlowAssignmentPromises);
            this.notify.success('time-off-v3.approvalFlowAssignedSuccessfully');
        } catch (e) {
            this.notify.error(e);
        }

        this.isLoading = false;

        this.timeOffApprovalFlowTable.reload();
    }

    private async deleteWorkSchedule(model: Model): Promise<void> {
        const approved = await this.workScheduleDeleteModal.show();

        if (!approved) {
            return;
        }

        await model.delete();
        this.workScheduleTable.reload();
        this.notify.success('time-off-v3.workScheduleDeletedSuccessfully');
    }

    private async assignWorkSchedule(model: Model): Promise<void> {
        const workSchedule = model as WorkSchedule;

        const currentlyAttachedEmployeeIds = workSchedule.employees.map((employee) => employee.id);

        const employeeIdsFromMassAssignment: number[] = (
            await this.appEmployeeSelection.show(workSchedule.employees)
        ).map((employee) => employee.id);

        const attachedEmployeesIds: number[] = employeeIdsFromMassAssignment.filter(
            (id) => !currentlyAttachedEmployeeIds.includes(id)
        );

        const detachedEmployeesIds: number[] = currentlyAttachedEmployeeIds.filter(
            (id) => !employeeIdsFromMassAssignment.includes(id)
        );

        await workSchedule.attach('employees', attachedEmployeesIds).detach('employees', detachedEmployeesIds).save();

        this.workScheduleTable.reload();
    }

    private async deleteApprovalFlow(model: Model): Promise<void> {
        const approved = await this.approvalFlowDeleteModal.show();

        if (!approved) {
            return;
        }

        await model.delete();
        this.timeOffApprovalFlowTable.reload();
        this.notify.success('time-off-v3.approvalFlowHasBeenDeletedSuccessfully');
    }

    private async onDeleteTimeOffType(model: Model): Promise<void> {
        try {
            const approved = await this.deleteTypeModal.show();

            if (!approved) {
                return;
            }
            this.isLoading = true;
            await model.delete();
            this.timeOffTypesTable.reload();
            this.notify.success('time-off-v3.timeOffTypeHasBeenDeleted');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private async getActiveAdmins(): Promise<void> {
        try {
            const [activeAdmins] = await Employee.param('company', this.auth.company.id)
                .orderBy('firstName', 'ASC')
                .where('adminOnly', true)
                .all();
            this.activeAdmins = activeAdmins;
        } catch (err) {
            this.notify.error(err);
        }
    }

    private async getDefaultApprover(): Promise<void> {
        try {
            const timeOffBackupApprover = await this.timeOffService.getTimeOffBackupApprover();

            this.selectedBackupApprover = timeOffBackupApprover.approverId ?? 0;

            this.currentTimeOffBackupApprover = new TimeOffBackupApprover(timeOffBackupApprover);
        } catch (err) {
            this.notify.error(err.message);
        }
    }

    private getWorkScheduleRoutePath(): string {
        return this.isTimeOffImprovementsFeatureFlagEnabled ? 'details' : 'edit';
    }
}
